import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ANT Design
import { Button, Form, Input, Segmented, Select, Switch, Modal, Popover, TreeSelect } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useStore } from '../../store/store';
import DocumentTypesSelector from '../common/DocumentTypesSelector';
import AdminOnlyFormItem from '../common/AdminOnlyFormItem';
import { getAvailableRootModels } from '../../requests/recursiveSections';

const selector = (state) => ({
  user: state?.user,
  currentTab: state.currentTab,
  folders: state.folders,
});

// Valeur sentinelle « aucun dossier » (racine). folder_id envoyé = null.
const ROOT_VALUE = '__root__';

const NewTemplateModal = ({ open, handleOk, handleCancel, type, defaultIsRecursive = false }) => {
  const { user, currentTab, folders } = useStore(selector);
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState(ROOT_VALUE);
  const [templatable, setTemplatable] = useState('opportunity');
  const [templatePattern, setTemplatePattern] = useState(defaultIsRecursive ? true : currentTab);
  const [landscape, setLandscape] = useState(false);
  const [work, setWork] = useState(null);
  const [documentTypeIds, setDocumentTypeIds] = useState([]);
  const [isRecursive, setIsRecursive] = useState(defaultIsRecursive);
  const [recursiveModelName, setRecursiveModelName] = useState(null);
  const [recursiveAgreementType, setRecursiveAgreementType] = useState(null);
  const [availableRootModels, setAvailableRootModels] = useState([]);
  const [adminOnly, setAdminOnly] = useState(false);

  const isSection = !!templatePattern;

  // Arbre des dossiers pour le sélecteur de destination (+ option Racine).
  const folderSelectData = useMemo(() => {
    const byParent = new Map();
    (folders || []).forEach((f) => {
      const key = f.parent_id ?? null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(f);
    });
    const build = (parentId) =>
      (byParent.get(parentId) || []).map((f) => ({
        title: f.name,
        value: f.id,
        children: build(f.id),
      }));
    return [{ title: '⤴ Racine (aucun dossier)', value: ROOT_VALUE }, ...build(null)];
  }, [folders]);

  const changeTemplatePatternValue = () => {
    const next = !templatePattern;
    setTemplatePattern(next);
    if (!next) {
      setIsRecursive(false);
      setRecursiveModelName(null);
      setRecursiveAgreementType(null);
    }
  };

  // Rattaché à une opportunité, un document sans métier n'apparaît dans aucune
  // liste (elles filtrent par métier) : il serait perdu. Le métier est donc
  // obligatoire, et choisi d'office quand la compagnie n'en a qu'un.
  const needsWork = templatable !== 'offer';
  const works = user?.works;
  useEffect(() => {
    if (needsWork && !work && works?.length === 1) setWork(works[0]);
  }, [needsWork, work, works]);

  // Une section récursive exige un modèle de données ; sans lui la création
  // partirait incomplète côté back.
  const canSubmit = name !== ''
    && !(isRecursive && !recursiveModelName)
    && !(needsWork && !work);

  const submit = () => {
    if (!canSubmit) return;
    handleOk({
      name,
      templatable,
      templatePattern,
      landscape,
      work,
      documentTypeIds,
      isRecursive,
      recursiveModelName,
      recursiveAgreementType,
      folderId: folderId === ROOT_VALUE ? null : folderId,
      adminOnly,
    });
    setName('');
    setFolderId(ROOT_VALUE);
    setIsRecursive(false);
    setRecursiveModelName(null);
    setRecursiveAgreementType(null);
    setAdminOnly(false);
  };

  useEffect(() => {
    if (templatable === 'offer') {
      setWork(null);
    }
  }, [templatable, templatePattern]);

  useEffect(() => {
    if (!isRecursive) {
      setRecursiveModelName(null);
      setRecursiveAgreementType(null);
      setAvailableRootModels([]);
      return;
    }
    getAvailableRootModels(work, templatable).then(({ root_models, agreement_type }) => {
      setAvailableRootModels(root_models);
      setRecursiveAgreementType(agreement_type);
      setRecursiveModelName(root_models.length === 1 ? root_models[0].value : null);
    });
  }, [isRecursive, work, templatable]);

  return (
    <Modal
      title={`${t('new-name')} ${type}`}
      open={open}
      onCancel={handleCancel}
      width={560}
      footer={[
        <Button key='back' onClick={handleCancel}>
          {t('cancel')}
        </Button>,
        <Button key='submit' type='primary' disabled={!canSubmit} onClick={submit}>
          {t('create')}
        </Button>,
      ]}
    >
      {/* Form uniquement pour la mise en page (labels alignés, espacements
          homogènes) : l'état reste porté par les useState ci-dessus. */}
      <Form layout='vertical' requiredMark={false} className='--hide-from-pdf'>
        <Form.Item label={t('name')} required>
          <Input
            autoFocus
            placeholder={t(type)}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={submit}
            value={name}
          />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item label='Rattachement'>
            <Segmented
              block
              value={templatable}
              onChange={setTemplatable}
              options={[
                { label: t('opportunity'), value: 'opportunity' },
                { label: t('offer'), value: 'offer' },
              ]}
            />
          </Form.Item>
          <Form.Item label='Orientation'>
            <Segmented
              block
              value={landscape ? 'landscape' : 'portrait'}
              onChange={(value) => setLandscape(value === 'landscape')}
              options={[
                { label: t('portrait-mode'), value: 'portrait' },
                { label: t('landscape'), value: 'landscape' },
              ]}
            />
          </Form.Item>
        </div>

        {needsWork && (
          <Form.Item label='Choix du métier' required>
            <Select
              value={work}
              onChange={(e) => setWork(e)}
              placeholder='Sélectionner un métier (obligatoire)'
              style={{ width: '100%' }}
              options={(works ?? []).map((item) => ({ value: item, label: t(item) }))}
            />
          </Form.Item>
        )}

        <Form.Item label={t('tags')}>
          <DocumentTypesSelector
            value={documentTypeIds}
            onChange={(e) => setDocumentTypeIds(e)}
          />
        </Form.Item>

        <Form.Item label='Dossier'>
          <TreeSelect
            value={folderId}
            onChange={setFolderId}
            treeData={folderSelectData}
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp='title'
            placeholder='Choisir un dossier'
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 320, overflow: 'auto' }}
          />
        </Form.Item>

        <AdminOnlyFormItem checked={adminOnly} onChange={setAdminOnly} />

        {/* Bloc « section » : le réglage récursif dépend de celui-ci, on les
            garde donc dans le même encart plutôt qu'en lignes séparées. */}
        <Form.Item style={{ marginBottom: 0 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '12px 14px',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Switch
                size='small'
                checked={isSection}
                onChange={changeTemplatePatternValue}
              />
              <span>{t('is_content')}</span>
            </div>

            {isSection && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Switch
                    size='small'
                    checked={isRecursive}
                    onChange={setIsRecursive}
                  />
                  <span>Section récursive</span>
                  <Popover
                    title='Section récursive'
                    content={
                      <div style={{ maxWidth: 320 }}>
                        <p style={{ marginBottom: 6 }}>
                          Une section répétée automatiquement : une instance par élément du modèle de
                          données (ex. un bloc par matériel ou par prestation de l'opportunité).
                        </p>
                        <p style={{ marginBottom: 0 }}>
                          Choisissez le <strong>modèle de données</strong> = le type d'élément répété.
                          Dans la section, insérez ses champs via <strong>@</strong>. Vous pourrez ensuite
                          imbriquer des sections enfants selon les relations autorisées par la convention.
                        </p>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#999', cursor: 'help' }} />
                  </Popover>
                </div>
                {isRecursive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ whiteSpace: 'nowrap' }}>Modèle de données</span>
                    <Select
                      placeholder='Sélectionner...'
                      value={recursiveModelName}
                      onChange={setRecursiveModelName}
                      style={{ flex: 1 }}
                      options={availableRootModels}
                      loading={availableRootModels.length === 0}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewTemplateModal;
